import { ColDef, ColumnMenuTab } from "ag-grid-enterprise";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { DateFilter, DateUtils } from "utils/dateutils";

const INITIAL_MAX_NUMBER_OF_MILESTONES = 10;

export const getMilestoneBySequenceNo = (params: any, sequenceNo: number) => {
  return params.data.date_block.milestones.find(
    (milestone: any) => milestone.sequence_no === sequenceNo
  );
};

export function getMaxNumberOfMilestonesFromEntityList(
  entityList: any[] | null
) {
  if (!entityList) return null;
  let lastMilestoneHasOffset = false;
  const milestonesCount = entityList.reduce((max: number, item: any) => {
    const lastMilestoneIndex = item.date_block.milestones.length - 1;
    if (item.date_block.milestones[lastMilestoneIndex]?.offset_name) {
      lastMilestoneHasOffset = true;
    }
    return Math.max(max, item.date_block.milestones.length);
  }, 0);
  return {
    milestonesCount,
    lastMilestoneHasOffset
  };
}

function getOffsetColumnDefs(
  milestoneArrayIndex: number,
  dbColsDefaultProperties: any
) {
  return [
    {
      colId: `offset_name_${milestoneArrayIndex}`,
      headerName: `Duration Name ${milestoneArrayIndex}`,
      ...dbColsDefaultProperties,
      valueGetter: (params: any) => {
        const milestoneBySequenceNo = getMilestoneBySequenceNo(
          params,
          milestoneArrayIndex
        );
        return milestoneBySequenceNo?.offset_name;
      }
    },
    {
      colId: `offset_duration_${milestoneArrayIndex}`,
      headerName: `Duration ${milestoneArrayIndex}`,
      ...dbColsDefaultProperties,
      valueGetter: (params: any) => {
        const milestoneBySequenceNo = getMilestoneBySequenceNo(
          params,
          milestoneArrayIndex
        );
        return milestoneBySequenceNo?.offset_duration;
      },
      filter: "agNumberColumnFilter",
      filterParams: numberFilterParams,
      editable: true,
      cellEditor: "agNumberCellEditor",
      cellEditorParams: numberCellEditorParams,
      cellClass: (params: any) => {
        const { context } = params;
        if (!context?.canInlineEditDBOffset) return "";
        const milestoneBySequenceNo = getMilestoneBySequenceNo(
          params,
          milestoneArrayIndex
        );
        return milestoneBySequenceNo?.is_editable ? "cell-editable" : "";
      }
    }
  ];
}

function featureDateBlockColumns(
  maxNumberOfMilestones: number | null,
  addLastOffsetColumns: boolean
) {
  const groupCols: ColDef[] = [
    {
      colId: "_milestones",
      headerName: "Milestones",
      width: 0,
      maxWidth: 0,
      headerClass: "!p-0 !w-0",
      filter: false,
      editable: false,
      menuTabs: [],
      suppressMovable: true,
      getQuickFilterText: () => "",
      // hide: true,
      lockPosition: "right"
    },
    {
      colId: "_durations",
      headerName: "Durations",
      width: 0,
      maxWidth: 0,
      headerClass: "!p-0 !w-0",
      filter: false,
      editable: false,
      menuTabs: [],
      suppressMovable: true,
      getQuickFilterText: () => "",
      // hide: true,
      lockPosition: "right"
    }
  ];

  const dbColsDefaultProperties = {
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    menuTabs: ["filterMenuTab"] as ColumnMenuTab[],
    editable: false,
    width: 180,
    lockPosition: "right" as const
  };

  const columnDefsForDateBlock: ColDef[] = [];

  for (
    let i = 0;
    i < (maxNumberOfMilestones || INITIAL_MAX_NUMBER_OF_MILESTONES);
    i += 1
  ) {
    const milestoneArrayIndex = i + 1;
    columnDefsForDateBlock.push(
      {
        colId: `milestone_name_${milestoneArrayIndex}`,
        headerName: `Milestone Name ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.name;
        }
      },
      {
        colId: `milestone_planned_date_${milestoneArrayIndex}`,
        headerName: `Planned Date ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.planned
            ? DateUtils.format(milestoneBySequenceNo?.planned)
            : undefined;
        },
        filter: CustomDateSetFilter,
        comparator: DateFilter.comparator
      },
      {
        colId: `milestone_projected_date_${milestoneArrayIndex}`,
        headerName: `Projected Date ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.projected
            ? DateUtils.format(milestoneBySequenceNo?.projected)
            : undefined;
        },
        filter: CustomDateSetFilter,
        comparator: DateFilter.comparator
      },
      {
        colId: `milestone_actual_date_${milestoneArrayIndex}`,
        headerName: `Actual Date ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.actual
            ? DateUtils.format(milestoneBySequenceNo?.actual)
            : undefined;
        },
        filter: CustomDateSetFilter,
        comparator: DateFilter.comparator
      }
    );

    const isLast =
      i === (maxNumberOfMilestones || INITIAL_MAX_NUMBER_OF_MILESTONES) - 1;

    if (!isLast) {
      columnDefsForDateBlock.push(
        ...getOffsetColumnDefs(milestoneArrayIndex, dbColsDefaultProperties)
      );
    }

    if (isLast && addLastOffsetColumns) {
      columnDefsForDateBlock.push(
        ...getOffsetColumnDefs(milestoneArrayIndex, dbColsDefaultProperties)
      );
    }
  }

  const dateBlockColumnIds = {
    milestones: columnDefsForDateBlock
      .map((col) => col.colId)
      .filter((col) => col?.includes("milestone_")),
    durations: columnDefsForDateBlock
      .map((col) => col.colId)
      .filter((col) => col?.includes("offset_"))
  };

  return {
    groupCols,
    columnDefsForDateBlock,
    dateBlockColumnIds
  };
}

export default featureDateBlockColumns;
